import DesignButton from 'components/button/DesignButton';
import DesignCard from 'components/card/DesignCard';
import DesignSelect from 'components/select/DesignSelect';
import { LinkIcon, PlusIcon } from 'components/svgs';
import { CapacityPlanRunSummary } from 'core/graphql/CapacityPlanning/models';
import { toast } from 'react-toastify';
import RunSelector from './RunSelector';
import { networkAgencyValue } from './useCapacityPlanUrl';

export type AgencyOption = { value: string; label: string };

/** Prepended to the agency list for headquarters users (ADR-0001). */
export const networkAgencyOption: AgencyOption = {
  value: networkAgencyValue,
  label: 'Network (all agencies)',
};

export interface CapacityPlanToolbarProps {
  agencies: AgencyOption[];
  /** Raw selection: a Guid, `network`, or null when none is selected. */
  agencySelection: string | null;
  areAgenciesLoading: boolean;
  onAgencyChange: (agency: string | null) => void;
  /** Only a headquarters user may list or create a Network Run. */
  isHeadquarters: boolean;
  runs: CapacityPlanRunSummary[];
  /** Guid of the Run the selector shows, or null when none is open. */
  selectedRunId: string | null;
  areRunsLoading: boolean;
  onRunChange: (runId: string | null) => void;
  canEdit: boolean;
  onNewRun: () => void;
}

/**
 * Agency and Run pickers plus the two page-level actions. Both fields stay
 * mounted and are only disabled, so the toolbar never changes height.
 */
const CapacityPlanToolbar = ({
  agencies,
  agencySelection,
  areAgenciesLoading,
  onAgencyChange,
  isHeadquarters,
  runs,
  selectedRunId,
  areRunsLoading,
  onRunChange,
  canEdit,
  onNewRun,
}: CapacityPlanToolbarProps) => {
  const options = isHeadquarters
    ? [networkAgencyOption, ...agencies]
    : agencies;
  const agency =
    options.find((option) => option.value === agencySelection) ?? null;

  const handleCopyLink = async () => {
    // The URL carries only the Agency, Run and tab ids - never customer data.
    if (!navigator.clipboard) {
      toast.error('Copying is not available here; use the address bar.');
      return;
    }

    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied');
    } catch {
      toast.error('Copying is not available here; use the address bar.');
    }
  };

  return (
    <DesignCard className="flex items-end gap-4">
      <div className="flex flex-col">
        <label
          className="text-sm text-light-black dark:text-sublte-white"
          htmlFor="capacityPlanAgencySelect"
        >
          Agency
        </label>
        <DesignSelect
          inputId="capacityPlanAgencySelect"
          name="capacityPlanAgency"
          className="w-80"
          isLoading={areAgenciesLoading}
          value={agency}
          options={options}
          isMulti={false}
          closeMenuOnSelect
          onChange={(option: AgencyOption | null) =>
            onAgencyChange(option ? option.value : null)
          }
        />
      </div>

      <RunSelector
        runs={runs}
        selectedRunId={selectedRunId}
        isLoading={areRunsLoading}
        onRunChange={onRunChange}
        disabled={agencySelection === null}
        className="w-[480px]"
      />

      <div className="flex-grow" />

      <DesignButton
        variant="ghost"
        disabled={agencySelection === null}
        onClickHandler={handleCopyLink}
      >
        <LinkIcon />
        Copy link
      </DesignButton>
      <DesignButton
        variant="primary"
        disabled={agencySelection === null || !canEdit}
        onClickHandler={onNewRun}
      >
        <PlusIcon />
        New run
      </DesignButton>
    </DesignCard>
  );
};

export default CapacityPlanToolbar;
